import {type MetaFunction} from '@remix-run/node';
import Competition, {type CompetitionContent} from '#/app/components/Competition.jsx';
import {Image} from '#/app/components/Image.jsx';
import {COMPARISON_CIRCUIT} from '#/app/content/alternatives/comparison.js';
import {createMetaHandler} from '#/app/helpers/createMetaHandler.js';
import {createMetaTags} from '#/app/helpers/createMetaTags.js';

const CONTENT: CompetitionContent = {
  company: 'Circuit',
  comparisonIndex: COMPARISON_CIRCUIT,
  highlights: [
    {
      description: [
        <div className="space-y-4" key="1">
          <p>Find the shortest, fastest, and most efficient delivery routes with a few clicks. Turn hours of work into minutes.</p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Consume 20% less fuel</li>
            <li>Lower CO2 emissions</li>
            <li>Reduce mileage</li>
            <li>Cut down on idle time</li>
            <li>Do more with less</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-6/12">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-control-center-1.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Pay less, do more',
      title: 'No more unnecessary costs',
    },
    {
      description: [
        <div className="space-y-4" key="1">
          <p>Run modern and tech-enabled operations. Gain full control over deliveries and empower your team.</p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Plan thousands of deliveries in the blink of an eye</li>
            <li>Re-optimize and reschedule in real-time</li>
            <li>Empower drivers with an easy-to-use mobile app</li>
            <li>Collect proof of delivery photos and notes</li>
            <li>Analyze performance and make data-driven decisions</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-8/12">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-integrations-2.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Let the AI do the work',
      title: 'AI-powered delivery operations',
    },
    {
      description: [
        <div className="space-y-4" key="1">
          <p>Impress your customers with a convenient and contemporary delivery experience. Cultivate lasting customer loyalty.</p>
          <ul className="ml-4 space-y-2 [&>*]:pl-4" style={{listStyleType: '"✓"'}}>
            <li>Provide live tracking and notifications to customers</li>
            <li>Collect immediate customer feedback upon delivery</li>
            <li>Predict delays and act in real-time</li>
            <li>Promote your brand, loyalty programs, coupons, and more</li>
            <li>Make communication between drivers and customers seamless</li>
          </ul>
        </div>,
      ],
      image: () => (
        <div className="flex w-11/12 items-center justify-center sm:w-full">
          <Image
            alt="ufleet native driver app screenshot showing a list of tasks"
            src="/features-customer-portal-2.png"
            style={{maxWidth: 500}}
          />
        </div>
      ),
      subtitle: 'Delight your customers',
      title: 'Exceptional delivery satisfaction',
    },
  ],
  quote: {
    image: '/case-studies/obi/marek-krol.jpg',
    person: 'Marek Król',
    quote:
      'Thanks to Ufleet, we no longer get questions from customers when our truck will be at the address. The transparent delivery process greatly improves the service quality of our logistics subcontractors.',
    title: 'OBI Store Director',
  },
  reasons: [
    {
      content: ['Tailor-made functionalities', 'Customized integrations', 'Branded Customer Portal'],
      title: 'Custom Product Functionality',
    },
    {
      content: [
        'Guided setup and implementation',
        'Always ready to help you maximize the platform benefits',
        'Efficient and responsive support',
      ],
      title: 'Exceptional Customer Care',
    },
    {content: ['All features included in the pricing', 'No setup or support fees', 'No hidden expenses'], title: 'Transparent Pricing'},
  ],
};

export const meta: MetaFunction = createMetaHandler(() => {
  const {company} = CONTENT;
  const slug = company.toLowerCase();
  return createMetaTags({
    description: `Compare the top delivery route optimization platforms – ${company} vs Ufleet – to decide which software is right for your business.`,
    title: `Best ${company} Alternative: Try Ufleet for Route Optimization - Ufleet`,
    url: `https://ufleet.io/${slug}-alternative-ufleet-${slug}`,
  });
});

export default function RoutificAlternative() {
  return <Competition {...CONTENT} />;
}
